import _ from 'lodash';
import { combineEpics, ofType } from 'redux-observable';
import { concat, forkJoin, merge, of } from 'rxjs';
import { filter, map, mergeMap, switchMap } from 'rxjs/operators';

import { PendingItemStatus } from '@appTypes/enums';
import { toPurchaseStatusesString } from '@modules/advancement/constants';
import { getCustomErrorMessage } from '@modules/bulkEntry/duck/utils';
import { tagTypes } from '@modules/rtk/constants';
import { esbApi } from '@modules/rtk/esb-api';
import tagFactory from '@modules/rtk/utils/tagFactory';
import {
  payloadStatusValues,
  unapproveAdvancementTypes,
} from '@shared/constants';
import { organizationGuidSel } from '@shared/duck/selectors/general.selectors';
import { toastService } from '@toasts';
import { userIdSel } from '@user';
import { catchAndReport } from '@utils/rxjs/operators';

import { recordAdvancementRequest } from '../../common';
import progressServices from './../../unapproveAdvancement/duck/services';
import {
  APPROVE_ADVANCEMENTS_REQUEST,
  DECLINE_ADVANCEMENTS_REQUEST,
  approveAdvancementsError,
  approveAdvancementsResponse,
  declineAdvancementsError,
  declineAdvancementsResponse,
} from './actions';
import { advancementStatusSel } from './selectors';
import services from './services';

const approveAdvancementsRequestEpic$ = (action$, state$) =>
  action$.pipe(
    /* Used in activity details screen */
    ofType(APPROVE_ADVANCEMENTS_REQUEST),
    filter(({ payload }) => payload.approveItems.length > 0),
    switchMap(({ payload }) => {
      const {
        approveItems,
        updatedValues,
        successMsg,
        approvedDate = undefined,
      } = payload;
      const state = state$.value;
      const loggedInUserId = userIdSel(state);
      const organizationGuid = organizationGuidSel(state);
      const status = advancementStatusSel(state);

      return services
        .approveItems$({
          loggedInUserId,
          organizationGuid,
          approveItems,
          updatedValues,
          status,
          approvedDate,
        })
        .pipe(
          mergeMap(() => {
            toastService.success(successMsg);
            const advancementTypes = approveItems.map(
              ({ advancementType }) => advancementType,
            );

            const statuses = _.chain([
              PendingItemStatus.STARTED,
              PendingItemStatus.COMPLETED,
            ])
              .uniq()
              .sort()
              .join(',')
              .value();

            const activitiesToInvalidate =
              approveItems.map(item =>
                tagFactory.Activities.activityItem(item.id),
              ) || [];

            const userSummary = approveItems.map(item =>
              tagFactory.UserActivitySummary.userSummary(item.userId),
            );

            return concat(
              of(
                approveAdvancementsResponse(advancementTypes),

                esbApi.util.invalidateTags([
                  tagFactory.AdvancementHistory.unitHistory(organizationGuid),
                  {
                    type: tagTypes.OrganizationAdvDashboard,
                    id: organizationGuid,
                  },
                  tagFactory.AdvancementHistory.unitHistoryWithStatus(
                    organizationGuid,
                    statuses,
                  ),
                  // To Award Tab
                  tagFactory.AdvancementHistory.unitHistoryWithStatus(
                    organizationGuid,
                    toPurchaseStatusesString,
                  ),
                  // to approve activity list
                  tagFactory.Activities.orgActivities(organizationGuid),
                  ...activitiesToInvalidate,
                  // counts for user
                  ...userSummary,
                  {
                    // to approve activity count
                    type: tagTypes.OrganizationActivitiesDashboard,
                    id: organizationGuid,
                  },
                  tagFactory.AdvancementHistory.toApproveTabRequirements(
                    organizationGuid,
                  ),
                ]),
              ),
            );
          }),
          catchAndReport(err => {
            const customError = getCustomErrorMessage(err.response);
            if (customError) {
              toastService.dismiss(undefined);
              toastService.error(customError);
            }
            return of(approveAdvancementsError(err));
          }),
        );
    }),
  );

const recordAdvancementsEpic$ = (action$, state$) =>
  action$.pipe(
    ofType(APPROVE_ADVANCEMENTS_REQUEST),
    filter(({ payload }) => payload.recordPersonsAdvancements.length > 0),
    map(({ payload }) => {
      const status = advancementStatusSel(state$.value);
      const {
        updatedValues: completionDates,
        recordSuccessMsg: successMsg,
        recordPersonsAdvancements: personsAdvancements,
      } = payload;

      const [item] = personsAdvancements;
      const { id: advancementId, advancementType } = item;

      return recordAdvancementRequest({
        successMsg,
        advancementId,
        advancementType,
        status,
        users: personsAdvancements,
        date: completionDates,
        closeOnSuccess: true,
      });
    }),
  );

const declineAdvancementsRequestEpic$ = (action$, state$) =>
  action$.pipe(
    ofType(DECLINE_ADVANCEMENTS_REQUEST),
    filter(({ payload }) => payload.approveItems.length > 0),
    switchMap(({ payload }) => {
      const { approveItems, successMsg } = payload;
      const state = state$.value;

      const organizationGuid = organizationGuidSel(state);

      const itemsWithRequirements = approveItems.filter(
        adv => adv.requirements.length > 0,
      );

      const unapproveRequirements = itemsWithRequirements.map(item => {
        const itemReq = item.requirements[0];

        return {
          advancementType: item.advancementType,
          advancementId: item.id,
          payload: [
            {
              userId: item.userId,
              requirements: [
                {
                  id: itemReq.id,
                  completed: false,
                  started: false,
                },
              ],
              organizationGuid: organizationGuid,
            },
          ],
        };
      });

      const filteredApproveItems = approveItems.filter(
        adv => !adv.requirements.length,
      );

      const advancementsToUnapprove = filteredApproveItems.map(item => {
        const advType = unapproveAdvancementTypes[item.advancementType];
        const nonAward = {
          userId: item.userId,
          organizationGuid: organizationGuid,
          advancementType: advType,
          advancementId: item.id,
          userAwardId: null,
          newStatus: payloadStatusValues.uncompleted,
        };

        return nonAward;
      });

      const uncompleteAdvancements$ = advancementsToUnapprove?.length
        ? progressServices.unapproveMultipleAdvancement$(
            advancementsToUnapprove,
          )
        : of(null);

      return uncompleteAdvancements$.pipe(
        mergeMap(() => {
          if (!unapproveRequirements?.length) {
            return of(null);
          }

          return forkJoin(
            unapproveRequirements.map(item =>
              progressServices.uncompleteMultipleRequirements$(item),
            ),
          );
        }),
        mergeMap(() => {
          toastService.success(successMsg);
          const advancementTypes = approveItems.map(
            ({ advancementType }) => advancementType,
          );

          const invalidate = [
            tagFactory.OrganizationAdvDashboard.unitDashboard(organizationGuid), // to  approve tab count
            tagFactory.AdvancementHistory.unitHistory(organizationGuid), // to approve list
            tagFactory.AdvancementHistory.unitHistoryWithStatus(
              organizationGuid,
              toPurchaseStatusesString,
            ), // to purchase #1 list
            ...advancementTypes.map(advancementType =>
              tagFactory.AdvancementHistory.unitHistoryByType(
                organizationGuid,
                advancementType,
              ),
            ),
          ];

          return merge(
            of(declineAdvancementsResponse(advancementTypes)),
            of(esbApi.util.invalidateTags(invalidate)),
          );
        }),
        catchAndReport(err => of(declineAdvancementsError(err))),
      );
    }),
  );

export default combineEpics(
  approveAdvancementsRequestEpic$,
  recordAdvancementsEpic$,
  declineAdvancementsRequestEpic$,
);
